import { cloneDeep, merge } from "lodash";
import { templateV1 } from "@/lib/schema";

const today = () => new Date().toISOString().slice(0, 10);
const n = (v) => Number(v) || 0;
const amt = (amount = 0, use = "N") => ({ amount, use });
const debt = (amount = 0, use = "Y", payment = 0) => ({ amount, payment, use });
const rec = () => ({ product: "", premium: "" });
const recSet = () => ({ life: rec(), di: rec(), ci: rec(), health: rec() });

export const person = (o = {}) => ({
  incomePct: 50, yearsNeeded: 20, incomeMethod: "Y", expenseMethod: "Y",
  permanentLife: 0, termLife: 0, groupLife: 0, criticalIllness: 0, ciYears: 1,
  diPct: 60, groupLtd: 0, individualDi: 0,
  health: { drugs: "", dental: "", vision: "", paramedical: "", hospital: "", travel: "" }, healthNotes: "", ...o,
});

export const newAccount = (id, type = "RRSP", value = 0, monthly = 0, ror = 5, use = "N", purpose = "Retirement", owner = "Client") => ({
  id, type, owner, value, monthly, ror, purpose, retirement: purpose === "Retirement" ? "Y" : "N", use,
});

export const v2Defaults = () => ({
  version: 2,
  profile: {
    name: "", dob: "", age: "", gender: "", maritalStatus: "", phone: "", email: "",
    address: "", city: "", province: "", meetingDate: today(), currency: "CAD", advisorName: "", remarks: "",
    retirementAge: 65, lifeExpectancy: 85, otherDependants: 0, householdNotes: "",
    spouse: { name: "", dob: "", age: "", gender: "", phone: "", email: "", retirementAge: 65, lifeExpectancy: 85 },
    children: [],
  },
  financial: {
    income: { clientEmployer: "", clientOccupation: "", clientAnnual: 0, spouseEmployer: "", spouseOccupation: "", spouseAnnual: 0, otherAnnual: 0, clientTakeHome: "", spouseTakeHome: "", otherTakeHome: "" },
    expenses: { items: [] },
    assets: { cash: amt(), primaryResidence: amt(), secondProperty: amt(), vehicles: amt(), other: amt() },
    liabilities: { mortgage: debt(), carLoan: debt(), loc: debt(), studentLoan: debt(), personalLoans: debt(), other: debt() },
  },
  investments: { accounts: [] },
  retirement: {
    cppMonthly: 0, yearsInCanada: 40, oasOverride: "", employerPensionMonthly: 0, otherMonthlyIncome: 0,
    otherIncomeExpected: 0, desiredMonthlyOverride: "", postRetRor: 4, targetRor: 8, currentSalary: 0,
    includeSpouse: "N",
    spouse: { cppMonthly: 0, yearsInCanada: 40, oasOverride: "", employerPensionMonthly: 0, otherMonthlyIncome: 0 },
  },
  recommendations: { client: recSet(), spouse: recSet(), retirement: rec() },
  assumptions: { inflation: 2.5 },
  insurance: {
    client: person(), spouse: person(),
    finals: { funeral: amt(0, "Y"), legal: amt(0, "Y"), emergency: amt(0, "Y"), education: amt(0, "Y"), otherTax: amt(0, "Y") },
  },
});

function fromV1(v1) {
  const P = v1.profile || {};
  const E = v1.expenses || {};
  const I = v1.insurance || {};
  const V = v1.investment || {};
  const ia = I.assets || {};
  const d = v2Defaults();
  Object.assign(d.profile, {
    name: P.name || "", age: P.age ?? "", meetingDate: P.meetingDate || today(), currency: P.currency || "CAD",
    advisorName: P.advisorName || "", remarks: P.remarks || "",
    retirementAge: V.retirementAge ?? 65, lifeExpectancy: V.lifeExpectancy ?? 85,
  });
  d.assumptions.inflation = E.inflation ?? 2.5;
  d.financial.expenses.items = cloneDeep(E.items || []);
  d.financial.income.clientAnnual = I.annualIncome ?? 0;
  d.financial.income.otherAnnual = 0;
  ["cash", "primaryResidence", "secondProperty"].forEach((k) => { d.financial.assets[k] = amt(ia[k]?.amount ?? 0, ia[k]?.use || "N"); });
  Object.entries(I.liabilities || {}).forEach(([k, x]) => { d.financial.liabilities[k] = debt(x.amount ?? 0, x.use || "Y"); });
  d.investments.accounts = [
    newAccount("a1", "RRSP", ia.rrsp?.amount ?? 0, n(V.rrspContrib) / 12, V.rrspRor ?? 10, ia.rrsp?.use || "N"),
    newAccount("a2", "TFSA", ia.tfsa?.amount ?? 0, n(V.tfsaContrib) / 12, V.tfsaRor ?? 10, ia.tfsa?.use || "N"),
    newAccount("a3", "Non-registered", ia.nonReg?.amount ?? 0, n(V.nonRegContrib) / 12, V.nonRegRor ?? 4, ia.nonReg?.use || "N"),
    newAccount("a4", "Pension", V.pensionValue ?? 0, ((n(V.pensionYour) + n(V.pensionEmployer)) * 26) / 12, V.pensionRor ?? 6, "N"),
  ];
  Object.assign(d.retirement, {
    cppMonthly: V.cppMonthly ?? 0, yearsInCanada: V.yearsInCanada ?? 40, employerPensionMonthly: V.employerPensionMonthly ?? 0,
    otherMonthlyIncome: V.otherMonthlyIncome ?? 0, otherIncomeExpected: V.otherIncomeExpected ?? 0,
    postRetRor: V.postRetRor ?? 4, targetRor: V.targetRor ?? 8, currentSalary: V.currentSalary ?? 0,
  });
  d.insurance.client = person({
    incomePct: I.incomePct ?? 50, yearsNeeded: I.yearsNeeded ?? 20, incomeMethod: I.incomeMethod || "Y", expenseMethod: I.expenseMethod || "Y",
    permanentLife: I.permanentLife ?? 0, termLife: I.termLife ?? 0, criticalIllness: I.criticalIllness ?? 0,
  });
  Object.entries(I.finals || {}).forEach(([k, x]) => { d.insurance.finals[k] = amt(x.amount ?? 0, x.use || "Y"); });
  d.legacyV1 = cloneDeep(v1);
  return d;
}

export function migrate(data) {
  if (!data || !Object.keys(data).length) return v2Defaults();
  if (data.version === 2) return merge(v2Defaults(), cloneDeep(data));
  return fromV1(data);
}

export const templateClient = (name) => {
  const d = migrate(templateV1(name));
  delete d.legacyV1;
  return d;
};

// New clients: same structure, formulas and assumptions as the template, with all client amounts empty
export const blankClient = (name) => {
  const d = templateClient(name);
  d.profile.age = "";
  d.financial.income.clientAnnual = 0;
  d.financial.expenses.items = d.financial.expenses.items.map((it) => ({ ...it, current: 0 }));
  Object.values(d.financial.assets).forEach((a) => { a.amount = 0; });
  Object.values(d.financial.liabilities).forEach((l) => { l.amount = 0; l.payment = 0; });
  d.investments.accounts = d.investments.accounts.map((a) => ({ ...a, value: 0, monthly: 0 }));
  Object.assign(d.retirement, { cppMonthly: 0, yearsInCanada: "", employerPensionMonthly: 0, otherMonthlyIncome: 0, otherIncomeExpected: 0, currentSalary: 0 });
  Object.assign(d.insurance.client, { permanentLife: 0, termLife: 0, criticalIllness: 0 });
  Object.values(d.insurance.finals).forEach((f) => { f.amount = 0; });
  return d;
};
